import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";

export default function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");
  const [accountName, setAccountName] = useState("");
  const [validationError, setValidationError] = useState("");

  const { register, isLoading, error } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationError("");
    if (password !== confirmPassword) {
      setValidationError("Passwords do not match.");
      return;
    }
    try {
      await register({ email, password, confirmPassword, userName: username, accountName });
      navigate("/");
    } catch {
      // error handled by store
    }
  };

  const inputClass =
    "w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";

  return (
    <div className="lw-register-page relative flex min-h-[calc(100vh-8rem)] items-center justify-center px-4 py-12 overflow-hidden">
      {/* Subtle dot-grid background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(hsl(var(--border)) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />
      {/* Radial brand glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 0%, hsl(346.8 77.2% 49.8% / 0.07), transparent)",
        }}
      />

      <div className="lw-register-wrapper relative z-10 w-full max-w-sm">
        {/* Logo */}
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <img src="/logo.svg" alt="ListWeave" width={32} height={32} className="h-8 w-8" />
          <span className="text-xl font-bold tracking-tight">ListWeave</span>
        </Link>

        {/* Heading */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold tracking-tight">Create account</h1>
          <p className="mt-1 text-sm text-muted-foreground">Get started with ListWeave</p>
        </div>

        {/* Error */}
        {(error || validationError) && (
          <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
            {validationError || error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div className="space-y-1.5">
            <label htmlFor="email" className="block text-sm font-medium">Email</label>
            <input
              id="email" name="email" type="email" autoComplete="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputClass} placeholder="you@example.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor="username" className="block text-sm font-medium">Username</label>
              <input
                id="username" name="username" type="text" autoComplete="username" required
                value={username} onChange={(e) => setUsername(e.target.value)}
                className={inputClass} placeholder="johndoe"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="accountName" className="block text-sm font-medium">Account</label>
              <input
                id="accountName" name="accountName" type="text" required
                value={accountName} onChange={(e) => setAccountName(e.target.value)}
                className={inputClass} placeholder="My Account"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="password" className="block text-sm font-medium">Password</label>
            <input
              id="password" name="password" type="password" autoComplete="new-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className={inputClass} placeholder="••••••••"
            />
            <p className="text-xs text-muted-foreground">At least 8 characters</p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="confirmPassword" className="block text-sm font-medium">Confirm Password</label>
            <input
              id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" required
              value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputClass} placeholder="••••••••"
            />
          </div>

          <div className="flex items-start gap-2 pt-0.5">
            <input
              id="terms" name="terms" type="checkbox" required
              className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-ring focus:ring-offset-1"
            />
            <label htmlFor="terms" className="text-sm text-muted-foreground leading-tight">
              I agree to the{" "}
              <a href="#" className="text-primary hover:text-primary/80 transition-colors">Terms</a>
              {" "}and{" "}
              <a href="#" className="text-primary hover:text-primary/80 transition-colors">Privacy Policy</a>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="lw-register-submit w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            {isLoading ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-primary hover:text-primary/80 transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
